import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { LiveLogo } from "@/components/site/LiveLogo";
import { WatchCard, WatchImage, Price, AvailBadge } from "@/components/site/WatchCard";

const CONCIERGE_IMG = "/concierge.webp";

export function ConciergeBand({ compact = false }) {
  const { t, r } = useT();
  return (
    <section className={`relative overflow-hidden border-y border-[#C8AE6E]/15 ${compact ? "my-16" : "mt-32"}`} data-testid="concierge-section">
      <div className="grid md:grid-cols-2">
        <div className="relative min-h-[280px] md:min-h-[460px] grain">
          <img src={CONCIERGE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" style={{ objectPosition: "35% center" }} loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0C0B09] hidden md:block" />
        </div>
        <div className="px-6 sm:px-14 py-14 md:py-20 flex flex-col justify-center">
          <p className="eyebrow">{t.concierge_eyebrow}</p>
          <h2 className="mt-5 text-3xl sm:text-4xl text-[#F3EFE6] leading-tight">{t.concierge_title}</h2>
          <p className="mt-6 text-[#A39E93] leading-relaxed max-w-md">{t.concierge_sub}</p>
          <Link to={`${r.orders}?tipo=especifico`} className="btn-gold mt-10 self-start" data-testid="concierge-cta-button">{t.concierge_cta} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

function FeaturedCarousel({ pieces, slideMs }) {
  const { t, r, lang } = useT();
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const timer = useRef(null);
  const n = pieces.length;

  useEffect(() => {
    if (n <= 1 || hover) return;
    timer.current = setTimeout(() => setIdx((i) => (i + 1) % n), slideMs);
    return () => clearTimeout(timer.current);
  }, [idx, n, hover, slideMs]);

  if (!n) return null;

  return (
    <section
      className="max-w-[1400px] mx-auto px-5 sm:px-10 mt-16"
      data-testid="featured-section"
      aria-roledescription="carousel"
      aria-label={t.featured_eyebrow}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <p className="eyebrow">{t.featured_eyebrow}</p>
        {n > 1 && (
          <div className="flex items-center gap-3" data-testid="featured-carousel-controls">
            <div className="flex items-center gap-2" role="tablist">
              {pieces.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === idx}
                  aria-label={`Destaque ${i + 1} de ${n}`}
                  onClick={() => setIdx(i)}
                  className="group relative h-[2px] w-10 overflow-hidden bg-[#282520]"
                  data-testid={`featured-dot-${i}`}
                >
                  <span
                    className={`absolute inset-y-0 left-0 bg-[#C8AE6E] transition-all ${i === idx ? "w-full" : i < idx ? "w-full opacity-30" : "w-0"} ${i === idx && !hover ? "featured-progress" : ""}`}
                    style={i === idx && !hover ? { animationDuration: `${slideMs}ms` } : {}}
                  />
                </button>
              ))}
            </div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#A39E93] tabular-nums">{String(idx + 1).padStart(2, "0")} <span className="text-[#282520]">/</span> {String(n).padStart(2, "0")}</span>
          </div>
        )}
      </div>

      <div className="relative mt-8">
        <div className="grid [&>*]:col-start-1 [&>*]:row-start-1">
          {pieces.map((w, i) => {
            const active = i === idx;
            return (
              <div
                key={w.id || w.slug}
                className={`grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-10 lg:gap-20 items-center w-full transition-opacity duration-[1400ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${active ? "opacity-100 pointer-events-auto z-10" : "opacity-0 pointer-events-none z-0"}`}
                aria-hidden={!active}
                data-testid={`featured-slide-${i}`}
              >
                <Link to={`${r.watch}/${w.slug}`} className="group block overflow-hidden">
                  <div className={`transition-transform duration-[7000ms] ease-out ${active ? "scale-[1.04]" : "scale-100"}`}>
                    <WatchImage image={w.image} alt={w.model} size="lg" />
                  </div>
                </Link>
                <div className={`transition-all duration-1000 pb-20 md:pb-0 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{ transitionDelay: active ? "250ms" : "0ms" }}>
                  <p className="eyebrow !text-[#A39E93]">{w.brand}</p>
                  <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl text-[#F3EFE6] leading-tight">{w.model}</h2>
                  <p className="mt-2 text-sm tracking-wider text-[#A39E93]">{w.reference}</p>
                  <p className="mt-8 text-[#F3EFE6]/75 leading-relaxed max-w-md italic font-display text-lg">{w[`tagline_${lang}`]}</p>
                  <div className="mt-8 flex items-center gap-5"><Price w={w} className="text-2xl" /><AvailBadge value={w.availability} /></div>
                  <Link to={`${r.watch}/${w.slug}`} className="btn-line mt-10" data-testid={`featured-view-button-${i}`}>{lang === "pt" ? "Descobrir a peça" : "Discover the piece"} <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, r } = useT();
  const { openAdvisor, settings } = useSite();
  const [recent, setRecent] = useState([]);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/public/watches", { params: { limit: 6 } }).then((x) => setRecent(x.data));
    const count = Math.max(1, Math.min(12, Number(settings?.featured_count) || 3));
    api.get("/public/watches", { params: { featured: true, limit: count } }).then(async (x) => {
      const slugs = (x.data || []).map((w) => w.slug);
      const details = await Promise.all(slugs.map((s) => api.get(`/public/watches/${s}`).then((d) => d.data).catch(() => null)));
      setFeatured(details.filter(Boolean));
    });
  }, [settings?.featured_count]);
  return (
    <div data-testid="home-page">
      <section className="relative min-h-[calc(100vh-72px)] flex items-center">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 w-full grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center py-16">
          <div className="reveal">
            <p className="eyebrow">{t.hero_eyebrow}</p>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F3EFE6] max-w-xl">{t.hero_title}</h1>
            <p className="mt-8 text-base text-[#A39E93] max-w-md leading-relaxed">{t.hero_sub}</p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to={r.catalog} className="btn-gold" data-testid="hero-catalog-button">{t.cta_catalog}</Link>
              {settings?.ai?.enabled && <button onClick={() => openAdvisor()} className="btn-line" data-testid="hero-advisor-button">{t.cta_advisor}</button>}
            </div>
          </div>
          <div className="reveal max-w-[560px] w-full mx-auto" style={{ animationDelay: "200ms" }}>
            <LiveLogo />
          </div>
        </div>
      </section>

      {featured.length > 0 && <FeaturedCarousel pieces={featured} slideMs={Math.max(3, Math.min(10, Number(settings?.featured_rotation_seconds) || 7)) * 1000} />}

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 mt-32" data-testid="recent-section">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div><p className="eyebrow">{t.recent_eyebrow}</p><h2 className="mt-4 text-3xl sm:text-4xl text-[#F3EFE6]">{t.recent_title}</h2></div>
          <Link to={r.catalog} className="text-xs uppercase tracking-[0.25em] text-[#C8AE6E] hover:text-[#E6D5AC] flex items-center gap-2" data-testid="recent-see-all">{t.see_all} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {recent.map((w, i) => <WatchCard key={w.id} w={w} delay={i * 90} />)}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 mt-32" data-testid="how-to-buy-section">
        <p className="eyebrow">{t.how_eyebrow}</p>
        <h2 className="mt-4 text-3xl sm:text-4xl text-[#F3EFE6]">{t.how_title}</h2>
        <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#282520]">
          {t.how.map(([h, p], i) => (
            <li key={h} className="bg-[#0C0B09] p-8 lg:p-10">
              <span className="font-display text-4xl text-[#C8AE6E]/60">{["I", "II", "III", "IV"][i]}</span>
              <h3 className="mt-6 text-xl text-[#F3EFE6]">{h}</h3>
              <p className="mt-3 text-sm text-[#A39E93] leading-relaxed">{p}</p>
            </li>
          ))}
        </ol>
      </section>

      <ConciergeBand />
    </div>
  );
}
